import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { AmBestRating } from 'src/app/interfaces/am-best-rating';
import { FinancialSize } from 'src/app/interfaces/financial-size';
import { InsurancePolicyTypeAbbreviated } from 'src/app/interfaces/insurance-policy-type-abbreviation';
import { InsurancePolicyStatus } from 'src/app/interfaces/insurance-policy.status';
import { dateGreaterThanOrEqualToTodayValidator } from 'src/app/validators/date-greater-than-today-validator';

import { InsurancePolicySiteForm } from './insurance-policy-site-form';

export class InsurancePolicyForm extends FormGroup<{
  id: FormControl<number | undefined>;
  InsuranceCertificateId: FormControl<number | undefined>;
  policyNumber: FormControl<string | null | undefined>;
  policyType: FormControl<InsurancePolicyTypeAbbreviated | undefined>;
  effectiveDate: FormControl<Date | null>;
  expirationDate: FormControl<Date | null>;
  status: FormControl<InsurancePolicyStatus | undefined>;
  statusDate: FormControl<Date | undefined>;
  insurer: FormControl<string | null | undefined>;
  insurerLetter: FormControl<string | null | undefined>;
  naic: FormControl<string | null>;
  aMBestCompanyNumber: FormControl<string | null>;
  amBestRating: FormControl<AmBestRating | null>;
  financialSize: FormControl<FinancialSize | null>;
  addlInsured: FormControl<boolean>;
  subrWvd: FormControl<boolean>;
  isRequired: FormControl<boolean>;
  hasPolicyDoc: FormControl<boolean>;
  hasRequiredForms: FormControl<boolean>;
  perOccurence: FormControl<number | null>;
  aggregate: FormControl<number | null>;
  hasAiOngoingOps: FormControl<boolean>;
  hasAiCompOps: FormControl<boolean>;
  hasPnc: FormControl<boolean>;
  damageToRented: FormControl<number | null>;
  medicalExpenses: FormControl<number | null>;
  personalAdvInjury: FormControl<number | null>;
  productOps: FormControl<number | null>;
  isProdOpsSameAsAgg: FormControl<boolean>;
  pollution: FormControl<number | null>;
  combinedSingle: FormControl<number | null>;
  biPerPerson: FormControl<number | null>;
  biPerAccident: FormControl<number | null>;
  propertyDamage: FormControl<number | null>;
  isCommercialAuto: FormControl<boolean>;
  isCaSplitLimit: FormControl<boolean>;
  hasNonOwnedAuto: FormControl<boolean>;
  elAnyExcluded: FormControl<boolean>;
  elEachAccident: FormControl<number | null>;
  elDiseaseEachEmployee: FormControl<number | null>;
  elDiseasePerPolicy: FormControl<number | null>;
  garageKeepers: FormControl<number | null>;
  onHookTowing: FormControl<number | null>;
  crimeDishonestyFidelityBond: FormControl<number | null>;
  wasMigrated: FormControl<boolean>;
  hasDescOps: FormControl<boolean>;
  nonOwnedAutoAmount: FormControl<number | null>;
  isContractualLiability: FormControl<boolean>;
  officerExcluded: FormControl<boolean>;
  isPerOccurrence: FormControl<boolean>;
  perProjLocAgg: FormControl<boolean>;
  primaryNonContributory: FormControl<boolean>;
  occurrenceBased: FormControl<boolean>;
  hasAdditionalInsuredEndorsements: FormControl<boolean>;
  hiredAutos: FormControl<number | null>;
  nonOwnedAutos: FormControl<number | null>;
  InsNameFailed: FormControl<boolean>;
  contingentAuto: FormControl<boolean>;
  trailerInterchange: FormControl<number | null>;
  cargoLiability: FormControl<number | null>;
  legalNameMismatch: FormControl<boolean>;
  isArchived: FormControl<boolean>;

  // Sites?: FormArray<SiteForm>
  InsurancePolicySites?: FormArray<InsurancePolicySiteForm>;
}> {
  constructor() {
    super({
      id: new FormControl<number | undefined>(
        { value: undefined, disabled: false },
        { nonNullable: true }
      ),
      InsuranceCertificateId: new FormControl<number | undefined>(
        { value: undefined, disabled: false },
        { nonNullable: true }
      ),
      policyNumber: new FormControl<string | null | undefined>(
        { value: undefined, disabled: false },
        { nonNullable: false, validators: [Validators.required] }
      ),
      policyType: new FormControl<InsurancePolicyTypeAbbreviated | undefined>(
        { value: undefined, disabled: false },
        { nonNullable: true, validators: [Validators.required] }
      ),
      effectiveDate: new FormControl<Date | null>(null, {
        validators: [Validators.required]
      }),
      expirationDate: new FormControl<Date | null>(null, {
        nonNullable: true,
        validators: [dateGreaterThanOrEqualToTodayValidator]
      }),
      status: new FormControl<InsurancePolicyStatus | undefined>(
        { value: undefined, disabled: false },
        { nonNullable: true, validators: [Validators.required] }
      ),
      statusDate: new FormControl<Date | undefined>(
        { value: undefined, disabled: false },
        { nonNullable: true }
      ),
      insurer: new FormControl<string | null | undefined>(
        { value: undefined, disabled: false },
        { nonNullable: false }
      ),
      insurerLetter: new FormControl<string | null | undefined>(
        { value: undefined, disabled: false },
        { nonNullable: false }
      ),
      naic: new FormControl<string | null>(null),
      aMBestCompanyNumber: new FormControl<string | null>(null),
      amBestRating: new FormControl<AmBestRating | null>(null),
      financialSize: new FormControl<FinancialSize | null>(null),
      addlInsured: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      subrWvd: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      isRequired: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      hasPolicyDoc: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      hasRequiredForms: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      perOccurence: new FormControl<number | null>(null),
      aggregate: new FormControl<number | null>(null),
      hasAiOngoingOps: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      hasAiCompOps: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      hasPnc: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      damageToRented: new FormControl<number | null>(null),
      medicalExpenses: new FormControl<number | null>(null),
      personalAdvInjury: new FormControl<number | null>(null),
      productOps: new FormControl<number | null>(null),
      isProdOpsSameAsAgg: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      pollution: new FormControl<number | null>(null),
      combinedSingle: new FormControl<number | null>(null),
      biPerPerson: new FormControl<number | null>(null),
      biPerAccident: new FormControl<number | null>(null),
      propertyDamage: new FormControl<number | null>(null),
      isCommercialAuto: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      isCaSplitLimit: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      hasNonOwnedAuto: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      elAnyExcluded: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      elEachAccident: new FormControl<number | null>(null),
      elDiseaseEachEmployee: new FormControl<number | null>(null),
      elDiseasePerPolicy: new FormControl<number | null>(null),
      garageKeepers: new FormControl<number | null>(null),
      onHookTowing: new FormControl<number | null>(null),
      crimeDishonestyFidelityBond: new FormControl<number | null>(null),
      wasMigrated: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      hasDescOps: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      nonOwnedAutoAmount: new FormControl<number | null>(null),
      isContractualLiability: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      officerExcluded: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      isPerOccurrence: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      perProjLocAgg: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      primaryNonContributory: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      occurrenceBased: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      hasAdditionalInsuredEndorsements: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      hiredAutos: new FormControl<number | null>(null, { nonNullable: true }),
      nonOwnedAutos: new FormControl<number | null>(null, {
        nonNullable: true
      }),
      InsNameFailed: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      contingentAuto: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      trailerInterchange: new FormControl<number | null>(null),
      cargoLiability: new FormControl<number | null>(null),
      legalNameMismatch: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      isArchived: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      })
    });
  }
}
